import type { BuiltInLanguage, ExtraSection, ExtraSectionStyle, SourceRow } from "./types";

const STYLE_BY_PREFIX: Record<string, ExtraSectionStyle> = {
  data: "default",
  datam: "m",
  datah: "h",
  datat: "t",
};

/** Text of a spreadsheet cell, including SheetJS rich-text/cell objects. */
function cellValueToText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "object") {
    const cell = value as { richText?: { text?: string }[]; w?: unknown; v?: unknown };
    if (Array.isArray(cell.richText)) return cell.richText.map((part) => part?.text ?? "").join("");
    if (typeof cell.w === "string" && cell.w.trim()) return cell.w;
    if (typeof cell.v === "string" && cell.v.trim()) return cell.v;
  }
  return String(value);
}

/**
 * Collects free-form detail sections from columns named `Data#Title`,
 * `Data_m#Title`, `Data_h#Title` or `Data_t#Title`. A `_NO`/`_EN` suffix
 * before the hash (e.g. `Data_t_EN#Title`) restricts it to one language.
 */
export function parseExtraSections(row: SourceRow): ExtraSection[] {
  const sections: ExtraSection[] = [];
  for (const key of Object.keys(row)) {
    // Tolerate Excel header quirks: zero-width chars, NBSP, full-width hash.
    const header = key
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\u00A0/g, " ")
      .trim();
    const hashIndex = header.search(/[＃#]/);
    if (hashIndex === -1) continue;
    const title = header.slice(hashIndex + 1).trim();
    let prefix = header.slice(0, hashIndex);
    let lang: BuiltInLanguage | null = null;
    const langMatch = prefix.trim().match(/^(.*?)[\s_-]+(NO|EN)$/i);
    if (langMatch) {
      prefix = langMatch[1]!;
      lang = langMatch[2]!.toLowerCase() === "en" ? "en" : "no";
    }
    const style = STYLE_BY_PREFIX[prefix.toLowerCase().replace(/[\s_-]+/g, "")];
    const text = cellValueToText(row[key]).replace(/\r\n?/g, "\n").trim();
    if (style && title && text) sections.push({ title, text, style, lang });
  }
  return sections;
}

/** Sections visible in `language` (language-less sections always show). */
export function getVisibleExtraSections(
  sections: ExtraSection[],
  language: string,
): ExtraSection[] {
  const lang = language === "en" ? "en" : "no";
  return sections.filter((section) => !section.lang || section.lang === lang);
}
